/**
 * SidePanelModal
 *
 * A right-side-panel variant of the shared <Modal> component.
 * Used exclusively by EnterpriseUploadModal — does NOT touch Modal.tsx,
 * so none of the 20-30 existing Modal usages are affected.
 *
 * Visual contract (matches Figma):
 *  • 543 px wide, anchored to the right edge of the viewport
 *  • Max-height 990 px; height = 100vh − 76px (top) − 14px (bottom)
 *  • Margins: top 76 px · right 10 px · bottom 14 px
 *  • Border-radius: 12 px on all corners
 *  • Blue (primary.main) title bar with white text + CloseIcon — identical
 *    to Modal when a string title is passed
 *  • Children rendered in a flex-column DialogContent so callers can pin a
 *    sticky footer by making the scrollable area flex:1 overflow:auto
 */

import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import React from 'react';

interface SidePanelModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** String → rendered with white text on blue bar (same as Modal).
   *  React node → rendered as-is inside the title bar. */
  title?: React.ReactNode;
  /** Optional extra controls placed left of the CloseIcon */
  titleActions?: React.ReactNode;
  /** Width of the panel. Defaults to 543px (Figma spec). */
  width?: string;
}

const SidePanelModal: React.FC<SidePanelModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  titleActions,
  width = '543px',
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={() => {}} // controlled externally via onClose
      disableEscapeKeyDown
      fullWidth
      disableScrollLock
      // Align container so the Paper sits at the right edge with room for margins
      sx={{
        zIndex: 1300,
        '& .MuiDialog-container': {
          justifyContent: 'flex-end',
          alignItems: 'flex-start', // let Paper respect its own top margin
        },
      }}
      PaperProps={{
        sx: {
          // ── Figma geometry ───────────────────────────────────────────
          // Width: 543 px
          // Max-height: 990 px
          // Margins: top 76 px · right 10 px · bottom 14 px
          // Border-radius: 12 px
          width,
          maxWidth: width,
          height: 'calc(100vh - 76px - 14px)', // respects top + bottom margin
          maxHeight: '990px',
          mt: '76px',
          mr: '10px',
          mb: '14px',
          ml: 0,
          borderRadius: '12px',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
          // ── Internal layout ──────────────────────────────────────────
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pointerEvents: 'auto',
        },
      }}
    >
      {/* ── Title bar — mirrors Modal's string-title styling exactly ── */}
      {title && (
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1,
            pb: 1,
            px: 2,
            // No mb:1 / sticky — panel header is always visible at the top
            backgroundColor: 'primary.main', // same token as Modal
            flexShrink: 0,
            zIndex: 10,
          }}
        >
          {/* Left: title text or node */}
          {typeof title === 'string' ? (
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>
              {title}
            </Typography>
          ) : (
            // React-node branch: render as-is (caller controls layout)
            <Box sx={{ flex: 1 }}>{title}</Box>
          )}

          {/* Right: optional actions + close button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {titleActions}
            <IconButton
              onClick={onClose}
              sx={{
                color: '#fff',
                '&:hover': { color: 'grey.100' },
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      {/*
       * DialogContent has p:0 so the caller (EnterpriseUploadModal) owns
       * the full flex layout — scrollable body + sticky footer.
       */}
      <DialogContent
        sx={{
          p: 0,
          overflowY: 'hidden', // inner Box handles scroll
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default SidePanelModal;
